import { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
} from "@heroui/react";
import { User } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { UnitDTO } from "shared/types/unit";
import { toast } from "react-toastify";
import SearchComponent, { SearchMode } from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";

interface InviteCandidateModalProps {
  isOpen: boolean;
  onClose: () => void;
  units?: UnitDTO[];
}

export const InviteCandidateModal = ({
  isOpen,
  onClose,
  units,
}: InviteCandidateModalProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedUnit, setSelectedUnit] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [unitError, setUnitError] = useState("");
  const { session } = useSession();

  // Check if we should use search component (when no units provided)
  const useSearchComponent = !units


  // Handle unit selection from SearchComponent
  const handleUnitSelect = (unit: any) => {
    setSelectedUnit(unit?.resourceId || "");
    setUnitError(""); // Clear error when unit is selected
  };

  const handleClearUnit = () => {
    setSelectedUnit("");
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setPhoneNumber("");
      setSelectedUnit("");
      setEmailError("");
      setFirstNameError("");
      setLastNameError("");
      setPhoneError("");
      setUnitError("");
    }
  }, [isOpen]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if form is complete and valid
  const isFormComplete = () => {
    return (
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      email.trim() !== "" &&
      validateEmail(email.trim()) &&
      phoneNumber.trim() !== "" &&
      selectedUnit !== ""
    );
  };

  const handleSubmit = async () => {
    // Reset errors
    setEmailError("");
    setFirstNameError("");
    setLastNameError("");
    setPhoneError("");
    setUnitError("");

    // Validation
    let hasErrors = false;

    if (!firstName.trim()) {
      setFirstNameError("Voornaam is verplicht");
      hasErrors = true;
    }

    if (!lastName.trim()) {
      setLastNameError("Achternaam is verplicht");
      hasErrors = true;
    }

    if (!email.trim()) {
      setEmailError("Email is verplicht");
      hasErrors = true;
    } else if (!validateEmail(email)) {
      setEmailError("Voer een geldig email adres in");
      hasErrors = true;
    }

    if (!phoneNumber.trim()) {
      setPhoneError("Telefoonnummer is verplicht");
      hasErrors = true;
    }

    if (!selectedUnit) {
      setUnitError("Unit selectie is verplicht");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/invite-candidate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            phoneNumber: phoneNumber.trim(),
            unitId: selectedUnit,
          }),
        },
        session
      );

      if (response.ok) {
        toast.success("Kandidaat uitnodiging is succesvol verzonden!");
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || "Er is een fout opgetreden bij het verzenden van de uitnodiging");
      }
    } catch (error) {
      console.error("Error inviting candidate:", error);
      toast.error("Er is een netwerk fout opgetreden");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      isDismissable={!loading}
      isKeyboardDismissDisabled={loading}
      className="font-normal text-neutral-primary"
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent className="overflow-visible">
        {(closeModal) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <User weight="duotone" size={24} className="text-moss-primary" />
                <h3 className="text-lg font-medium">Kandidaat uitnodigen</h3>
              </div>
              <p className="text-sm text-neutral-secondary">
                Stuur een uitnodiging naar een nieuwe kandidaat
              </p>
            </ModalHeader>
            <ModalBody className="overflow-visible">
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="text"
                    label="Voornaam"
                    placeholder="Voornaam"
                    value={firstName}
                    onValueChange={setFirstName}
                    isInvalid={!!firstNameError}
                    errorMessage={firstNameError}
                    isRequired
                    classNames={{
                      input: "text-neutral-primary",
                      label: "text-neutral-primary font-medium",
                    }}
                  />
                  <Input
                    type="text"
                    label="Achternaam"
                    placeholder="Achternaam"
                    value={lastName}
                    onValueChange={setLastName}
                    isInvalid={!!lastNameError}
                    errorMessage={lastNameError}
                    isRequired
                    classNames={{
                      input: "text-neutral-primary",
                      label: "text-neutral-primary font-medium",
                    }}
                  />
                </div>

                <Input
                  type="email"
                  label="Email adres"
                  placeholder="kandidaat@example.com"
                  value={email}
                  onValueChange={setEmail}
                  isInvalid={!!emailError}
                  errorMessage={emailError}
                  isRequired
                  classNames={{
                    input: "text-neutral-primary",
                    label: "text-neutral-primary font-medium",
                  }}
                />

                <Input
                  type="tel"
                  label="Telefoonnummer"
                  placeholder="+32 123 45 67 89"
                  value={phoneNumber}
                  onValueChange={setPhoneNumber}
                  isInvalid={!!phoneError}
                  errorMessage={phoneError}
                  isRequired
                  classNames={{
                    input: "text-neutral-primary",
                    label: "text-neutral-primary font-medium",
                  }}
                />

{useSearchComponent ? (
                  <div className="flex flex-col gap-1">
                    <SearchComponent
                      type="regular"
                      label="Unit"
                      resourceFilter={[ResourceType.UNIT]}
                      onSelect={handleUnitSelect}
                      isCleared={handleClearUnit}
                      mode={SearchMode.SEARCH_AND_REDIRECT}
                      hasEditToggle={false}
                      classNames={unitError ? "border-danger" : ""}
                    />
                    {unitError && (
                      <span className="text-tiny text-danger">{unitError}</span>
                    )}
                  </div>
                ) : (
                  <Select
                    label="Unit"
                    placeholder="Selecteer een unit..."
                    selectedKeys={selectedUnit ? new Set([selectedUnit]) : new Set()}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys as Set<string>)[0];
                      setSelectedUnit(selectedKey || "");
                      setUnitError(""); // Clear error when unit is selected
                    }}
                    isInvalid={!!unitError}
                    errorMessage={unitError}
                    isRequired
                    classNames={{
                      base: "w-full",
                      listbox: "max-h-[200px]",
                      popoverContent: "w-full",
                    }}
                  >
                    {(units || [])
                      .filter((unit) => unit.id) // Filter out units without ID
                      .map((unit) => (
                        <SelectItem
                          key={unit.id!}
                          textValue={`${unit.id} ${unit.referencePlus ? `(${unit.referencePlus})` : ''}`}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{unit.id}</span>
                            {unit.referencePlus && (
                              <span className="text-sm text-neutral-secondary">{unit.referencePlus}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                  </Select>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="flex gap-3">
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={closeModal}
                  label="Annuleren"
                />
                <ButtonNew
                  variant="primaryMoss"
                  onClick={handleSubmit}
                  label={loading ? "Bezig..." : "Uitnodiging verzenden"}
                  isDisabled={!isFormComplete() || loading}
                />
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
